import {
  OFRA_VOLUME_CONTAINER_OPTIONS,
  OFRA_FERTILIZER_TYPE_OPTIONS,
  OFRA_APPLICATION_METHOD_OPTIONS,
} from "../constants/ofraCalculatorOptions";

// ------------------------------------------------------------------------------------
// How much fertilizer each application method spreads per unit area.
// These numbers come directly from the official OFRA Excel sheet.
// Point applies less fertilizer per spot, so its factor is smaller.
// Band and Broadcast cover wider areas, so their factors are larger.
// ------------------------------------------------------------------------------------
const APPLICATION_METHOD_FACTORS = {
  point: 0.1875,
  band: 0.75,
  broadcast: 0.75,
};

// ------------------------------------------------------------------------------------
// Tidies up a number before showing it to the user.
// - Rounds to a maximum of 2 decimal places (e.g. 105.6667 → 105.67)
// - Removes unnecessary trailing zeros (e.g. 80.00 → 80)
// - Returns a dash "—" if the number is somehow invalid (e.g. divided by zero)
// ------------------------------------------------------------------------------------
export function formatOfraNumber(value) {
  if (!isFinite(value) || isNaN(value)) return "—";
  return parseFloat(value.toFixed(2)).toString();
}

// ------------------------------------------------------------------------------------
// THE MAIN CALCULATION
//
// Given the four inputs the user selects/types, this function works out how many
// points (or meters) of fertilizer one container covers.
//
// The formula (from the OFRA Excel sheet) is:
//
//   Result = (Container Volume ÷ ((Fertilizer Rate ÷ 10) × Application Factor)) × Fertilizer Density
//
// Plain English version:
//   1. Divide the fertilizer rate by 10  →  converts kg/ha to a workable scale
//   2. Multiply that by the application method factor  →  accounts for spacing
//   3. Divide the container volume by that number  →  gives base coverage
//   4. Multiply by the fertilizer density  →  converts volume to weight-based coverage
//
// The function returns one of three outcomes:
//   "empty"   — the user hasn't filled in anything yet, so show nothing
//   "invalid" — something is missing or wrong, so show a helpful error message
//   "valid"   — everything looks good, so show the result
// ------------------------------------------------------------------------------------
export function calculateOfraFertilizerRate({
  containerValue,
  fertilizerTypeValue,
  fertilizerRateValue,
  applicationMethodValue,
}) {
  // Check if the user has touched any field at all.
  // If nothing has been filled in, there is nothing to calculate yet.
  const hasAnyInput =
    containerValue !== "" ||
    fertilizerTypeValue !== "" ||
    fertilizerRateValue !== "" ||
    applicationMethodValue !== "";

  if (!hasAnyInput) {
    return { status: "empty" };
  }

  // Look up the container the user selected (e.g. Peak Milk Can = 180 mL).
  // If it doesn't match any known container, ask them to pick one.
  const containerOption = OFRA_VOLUME_CONTAINER_OPTIONS.find((o) => o.value === containerValue);
  if (!containerOption) {
    return { status: "invalid", validationKey: "missingContainer" };
  }

  // Look up the fertilizer type the user selected (e.g. Urea = 1.32 g/mL density).
  // If it doesn't match any known type, ask them to pick one.
  const fertilizerOption = OFRA_FERTILIZER_TYPE_OPTIONS.find(
    (o) => o.value === fertilizerTypeValue,
  );
  if (!fertilizerOption) {
    return { status: "invalid", validationKey: "missingFertilizerType" };
  }

  // Read the fertilizer rate the user typed in (e.g. 120 kg/ha).
  // It must be a positive number — zero or negative makes no sense here.
  const fertilizerRate = parseFloat(fertilizerRateValue);
  if (fertilizerRateValue === "" || isNaN(fertilizerRate) || fertilizerRate <= 0) {
    return { status: "invalid", validationKey: "missingFertilizerRate" };
  }

  // Look up the application method the user selected (e.g. Point).
  // This also carries the spacing labels and values shown in the detail panel.
  const methodOption = OFRA_APPLICATION_METHOD_OPTIONS.find(
    (o) => o.value === applicationMethodValue,
  );
  if (!methodOption) {
    return { status: "invalid", validationKey: "missingApplicationMethod" };
  }

  // Get the numeric factor for the chosen method from our constants above.
  const applicationFactor = APPLICATION_METHOD_FACTORS[applicationMethodValue];
  if (applicationFactor === undefined) {
    return { status: "invalid", validationKey: "missingApplicationMethod" };
  }

  // Pull out the two key numbers we need for the formula.
  const containerVolumeMl = containerOption.volumeMl; // e.g. 180 mL
  const fertilizerDensity = fertilizerOption.densityGPerMl; // e.g. 1.32 g/mL

  // Step 1 & 2: Calculate the divisor — (FertilizerRate ÷ 10) × ApplicationFactor
  // e.g. (120 ÷ 10) × 0.1875 = 2.25
  const divisor = (fertilizerRate / 10) * applicationFactor;

  // Guard against dividing by zero (shouldn't happen with valid inputs, but just in case).
  if (divisor === 0) {
    return { status: "invalid", validationKey: "divisionByZero" };
  }

  // Step 3 & 4: Final result — (ContainerVolume ÷ divisor) × FertilizerDensity
  // e.g. (180 ÷ 2.25) × 1.32 = 105.6
  const calculatedValue = (containerVolumeMl / divisor) * fertilizerDensity;

  // Return everything the UI needs to display the result and method detail panel.
  return {
    status: "valid",
    calculatedValue,
    spacingLabelKey: methodOption.spacingLabel,
    spacingValue: methodOption.spacingValue,
    secondaryLabelKey: methodOption.secondaryLabel,
    secondaryValue: methodOption.secondaryValue,
    resultLabelKey: methodOption.resultLabel,
  };
}
